import { getUITranslation } from "../utils/get_ui_translation";
import * as Toast from "../framework/components/toast";
import { fetchJSON } from "../framework/api";

/* global Bloodhound */

function onSelectCompanyResult(ev, suggestion) {
  const parentForm = ev.target.closest(".js-company-form");
  if (!parentForm) return;

  // Mapping a form field identifier on the left to the expected value in the selected suggestion
  const fieldMap = {
    name: suggestion.denomination,
    legal_name: suggestion.denomination,
    vat: suggestion.vat,
    ape: suggestion.ape,
    siret: suggestion.siret,
    // Address
    road_address: [suggestion.address_1, suggestion.address_2].filter(Boolean).join(", "),
    city: suggestion.city,
    city_code: suggestion.city_code,
  };

  Object.entries(fieldMap).forEach(([field, value]) => {
    if (value == null) return;

    const input = parentForm.querySelector(`[name="company[${field}]"]`);
    if (!input) return;

    input.value = value;
    input.dispatchEvent(new Event("change", { bubbles: true }));

    const inputVisualWrapper = input.closest(".text-input");
    inputVisualWrapper.classList.add("has-bg-purple-10");
  });
}

//----------------------------------------------------------------------------------------
// initPappersCompanyAutocomplete
//----------------------------------------------------------------------------------------
function getCountrySelect(autocompleteElt) {
  const form = autocompleteElt.closest(".js-company-form");
  return form?.querySelector('[name="company[country_code]"]');
}

function initPappersCompanyAutocomplete(autocompleteElt) {
  const countrySelect = getCountrySelect(autocompleteElt);

  const pappersBloodhound = new Bloodhound({
    datumTokenizer: function (datum) {
      return Bloodhound.tokenizers.whitespace(datum.nom_entreprise);
    },
    queryTokenizer: Bloodhound.tokenizers.whitespace,
    remote: {
      url: "/ajax/pappers/search",
      prepare: function (query, settings) {
        const countryCode = countrySelect ? countrySelect.value : "";
        settings.url = `/ajax/pappers/search?q=${encodeURIComponent(query)}&country_code=${encodeURIComponent(countryCode)}`;
        return settings;
      },
      rateLimitWait: 500,
      transport: function (settings, onSuccess, onError) {
        fetchJSON(settings.url)
          .then(onSuccess)
          .catch(function (error) {
            if (error.code === 429) {
              Toast.show(
                "#toast-template-error",
                getUITranslation("Limite de recherches d'entreprises atteinte, réessayez plus tard")
              );
            }

            onError(error);
          });
      },
      transform: function (response) {
        return response.companies;
      },
    },
  });

  $(autocompleteElt)
    .typeahead(
      {
        hint: false,
        highlight: true,
        minLength: 3,
      },
      {
        name: "pappers-companies",
        source: pappersBloodhound,
        display: function (datum) {
          return datum.nom_entreprise;
        },
        limit: 10,
        templates: {
          notFound: `
          <div class="label label--md has-p-03">
            ${getUITranslation("Aucune entreprise trouvée")}
          </div>
        `,
          suggestion: function (datum) {
            const suggestionEl = document.createElement("div");
            suggestionEl.classList.add("has-flex", "has-justify-between", "has-gap-03");

            // Build the left side: "{CompanyName} ({headquartersCityCode})"
            const leftSideEl = document.createElement("div");
            leftSideEl.classList.add("has-flex-initial");
            const nameEl = document.createElement("span");
            nameEl.textContent = datum.nom_entreprise;
            leftSideEl.appendChild(nameEl);

            const cityCodeEl = document.createElement("span");
            if (datum.city_code) {
              cityCodeEl.textContent = `(${datum.city_code})`;
              cityCodeEl.classList.add("has-text-grey-80", "has-ml-01");
              leftSideEl.appendChild(cityCodeEl);
            }

            suggestionEl.appendChild(leftSideEl);

            // Build the right side: "{Libellé APE}"
            if (datum.libelle_ape) {
              const rightSideEl = document.createElement("div");
              rightSideEl.textContent = datum.libelle_ape;
              rightSideEl.setAttribute("title", datum.libelle_ape);
              rightSideEl.classList.add(
                "has-flex-1",
                "has-basis-1/3",
                "has-italic",
                "has-text-grey-80",
                "has-text-right",
                "has-whitespace-nowrap",
                "has-overflow-hidden",
                "has-text-ellipsis"
              );
              suggestionEl.appendChild(rightSideEl);
            }

            return suggestionEl.outerHTML;
          },
        },
      }
    )
    .on("typeahead:select", onSelectCompanyResult);
}

//----------------------------------------------------------------------------------------
// Setup: enable/disable autocomplete based on country
//----------------------------------------------------------------------------------------
function setupPappersAutocomplete(autocompleteElt) {
  const countrySelect = getCountrySelect(autocompleteElt);
  let isInitialized = false;

  function enable() {
    if (isInitialized) return;
    initPappersCompanyAutocomplete(autocompleteElt);
    isInitialized = true;
  }

  function disable() {
    if (!isInitialized) return;
    $(autocompleteElt).typeahead("destroy");
    isInitialized = false;
  }

  // "FR" + French DROM-COM using SIRET/SIREN and covered by Pappers)
  const PAPPERS_SUPPORTED_COUNTRY_CODES = ["FR", "GP", "MQ", "GF", "RE", "YT", "PM", "BL", "MF", "WF", "PF", "NC", "TF"];

  if (countrySelect && PAPPERS_SUPPORTED_COUNTRY_CODES.includes(countrySelect.value)) {
    enable();
  }

  if (countrySelect) {
    countrySelect.addEventListener("change", () => {
      if (PAPPERS_SUPPORTED_COUNTRY_CODES.includes(countrySelect.value)) {
        enable();
      } else {
        disable();
      }
    });
  }
}

//----------------------------------------------------------------------------------------
// Init on load
//----------------------------------------------------------------------------------------
document.addEventListener("DOMContentLoaded", () => {
  document.querySelectorAll("[data-autocomplete='pappers-company']").forEach((autocompleteElt) => {
    setupPappersAutocomplete(autocompleteElt);
  });
});
